import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { colors } from '../styles/colors';
import { useLoginAnimation } from '../hooks/useLoginAnimation';
import LoginLogo from '../components/LoginLogo';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  const { login, loginWithPasskey, setShowWelcomeToast } = useAuth();
  const navigate = useNavigate();
  const { showFullLogo, showForm } = useLoginAnimation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      if (result.success) {
        if (result.forcePasswordChange) {
          sessionStorage.setItem('temp_password', password);
          navigate('/forced-password-change');
        } else {
          setShowWelcomeToast(true);
          navigate('/');
        }
      } else {
        setError(result.error || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setIsPasskeyLoading(true);
    try {
      const result = await loginWithPasskey(rememberMe);
      if (result.success) {
        if (result.forcePasswordChange) {
          navigate('/forced-password-change');
        } else {
          setShowWelcomeToast(true);
          navigate('/');
        }
      } else {
        setError(result.error || 'Connexion avec clé échouée');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <LoginLogo showFullLogo={showFullLogo} />

      <div
        style={{
          ...styles.formContainer,
          opacity: showForm ? 1 : 0,
          transform: showForm ? 'translateY(0)' : 'translateY(20px)'
        }}
      >
        <h1 style={styles.title}>Connexion</h1>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={styles.input}
              disabled={isLoading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>Mot de passe</label>
            <div style={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={styles.input}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
                disabled={isLoading}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={styles.checkboxContainer}>
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={styles.checkbox}
              disabled={isLoading}
            />
            <label htmlFor="rememberMe" style={styles.checkboxLabel}>
              Se souvenir de moi
            </label>
          </div>

          {error && <div style={styles.errorMessage}>{error}</div>}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...styles.button,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Connexion...' : 'Se connecter'}
          </button>

          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={isLoading || isPasskeyLoading}
            style={{
              ...styles.passkeyButton,
              opacity: (isLoading || isPasskeyLoading) ? 0.6 : 1,
              cursor: (isLoading || isPasskeyLoading) ? 'not-allowed' : 'pointer',
            }}
          >
            <KeyRound size={18} color="#ffffff" style={styles.keyIcon} />
            {isPasskeyLoading ? 'Vérification...' : 'Clé de sécurité'}
          </button>

          <Link to="/forgot-password" style={styles.forgotPasswordLink}>
            Mot de passe oublié ?
          </Link>
        </form>
      </div>

      <style>{`
        input:focus {
          outline: none;
          border-color: ${colors.primary.teal};
          box-shadow: 0 0 0 3px ${colors.primary.teal}20;
        }
        button:hover:not(:disabled) {
          background-color: ${colors.primary.teal};
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        button:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: colors.text.white,
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '"Noto Sans", sans-serif',
    paddingTop: '4rem',
  },
  formContainer: {
    backgroundColor: colors.secondary.lightGray,
    padding: '3rem',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '400px',
    marginTop: '100px',
    transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out'
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: colors.primary.gray,
    textAlign: 'center',
    marginBottom: '2rem',
    marginTop: 0
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.primary.gray
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: `1px solid ${colors.secondary.mediumGray}`,
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    boxSizing: 'border-box'
  },
  passwordWrapper: {
    position: 'relative',
  },
  eyeButton: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    color: colors.secondary.mediumGray
  },
  button: {
    width: '100%',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.text.white,
    backgroundColor: colors.primary.teal,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    marginTop: '0.5rem'
  },
  passkeyButton: {
    position: 'relative',
    width: '100%',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.text.white,
    backgroundColor: colors.primary.gold,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '-1rem',
  },
  errorMessage: {
    padding: '0.75rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: '6px',
    fontSize: '0.875rem',
    textAlign: 'center',
    border: '1px solid #fcc'
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '-0.5rem'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: colors.primary.teal
  },
  checkboxLabel: {
    fontSize: '0.875rem',
    color: colors.primary.gray,
    cursor: 'pointer',
    userSelect: 'none'
  },
  keyIcon: {
    position: 'absolute',
    left: '0.875rem',
  },
  forgotPasswordLink: {
    display: 'block',
    textAlign: 'center',
    color: colors.primary.teal,
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginTop: '0rem',
    transition: 'opacity 0.2s'
  },
};

export default LoginPage;
